import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { htmlEncode } from '../../../utils/escape.utils';
import { MaintenanceWindowSchedulerSchema } from '../types';
import { FrequencyOptions, RecurrenceOptions, WeekDays } from '../../../types/common/schedulerTypes';
import { GzFormFieldWebComponent } from '../../../ui/gzForm/formField';
import { GzDropdownWebComponent } from '../../../ui/gzDropdown';
import { GzInputNumberWebComponent } from '../../../ui/gzInput';
import { FormGroup } from '../../../ui/gzForm/formGroup/form-group';
import { GzMultiSelectGroupWebComponent } from '../../../ui/gzMultiSelect';
import { GzTimePickerWebComponent } from '../../../ui/gzTimePicker';
import { GzDatePickerWebComponent } from '../../../ui/gzDatePicker';
import { RequiredValidator } from '../../../ui/gzForm/validators/required.validator';
import { TimeIntervalValidator } from '../../../ui/gzForm/validators/time-interval.validator';

export class MaintenanceWindowSchedulerView extends BaseWebComponentView {
  public formGroup!: FormGroup;

  public dom: {
    $wrapper: HTMLElement;

    $schedulerTitle: HTMLElement;

    $recurrenceFormField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;
    // Weekly
    $recurrenceWeeklyOptionsContainer: HTMLElement;
    $appliesEveryWeeksFormField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;
    $appliesEveryWeeksDescription: HTMLElement;
    // On the following day(s) - setup day selector
    $weeklyOnFormField: GzFormFieldWebComponent<GzMultiSelectGroupWebComponent, Array<string | number>>;

    // Monthly
    $recurrenceMonthlyOptionsContainer: HTMLElement;
    $appliesEveryMonthsFormField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;
    $appliesEveryMonthsDescription: HTMLElement;
    // On
    $monthlyOnLabeledContent: HTMLElement;
    $monthlyFrequencyFormField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;
    $monthlyFrequencyDayFormField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;
    $monthlyFrequencyDayNumberFormField: GzFormFieldWebComponent<GzInputNumberWebComponent, number>;

    $commonOptionsContainer: HTMLElement;
    $startingWithFormField: GzFormFieldWebComponent<GzDatePickerWebComponent, any>;
    $betweenLabeledContent: HTMLElement;
    $betweenStartFormField: GzFormFieldWebComponent<GzTimePickerWebComponent, any>;
    $betweenDescription: HTMLElement;
    $betweenEndFormField: GzFormFieldWebComponent<GzTimePickerWebComponent, any>;
  };

  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('.wrapper'),
      $schedulerTitle: this.shadow.querySelector('#scheduler-title'),
      $recurrenceFormField: this.shadow.querySelector('#recurrence-form-field'),
      // Weekly
      $recurrenceWeeklyOptionsContainer: this.shadow.querySelector('#recurrence-weekly-container'),
      $appliesEveryWeeksFormField: this.shadow.querySelector('#applies-every-weeks-form-field'),
      $appliesEveryWeeksDescription: this.shadow.querySelector('#applies-every-weeks-form-field gz-title'),
      // On the following day(s) - setup day selector
      $weeklyOnFormField: this.shadow.querySelector('#weekly-on-form-field'),

      // Monthly
      $recurrenceMonthlyOptionsContainer: this.shadow.querySelector('#recurrence-monthly-container'),
      $appliesEveryMonthsFormField: this.shadow.querySelector('#applies-every-months-form-field'),
      $appliesEveryMonthsDescription: this.shadow.querySelector('#applies-every-months-form-field gz-title'),
      // On
      $monthlyOnLabeledContent: this.shadow.querySelector('#monthly-on-labeled-content gz-title'),
      $monthlyFrequencyFormField: this.shadow.querySelector('#monthly-frequency-form-field'),
      $monthlyFrequencyDayFormField: this.shadow.querySelector('#monthly-frequency-day-form-field'),
      $monthlyFrequencyDayNumberFormField: this.shadow.querySelector('#monthly-frequency-day-number-form-field'),

      $commonOptionsContainer: this.shadow.querySelector('#common-fields-container'),
      $startingWithFormField: this.shadow.querySelector('#starting-with-form-field'),
      $betweenLabeledContent: this.shadow.querySelector('#between-label'),
      $betweenStartFormField: this.shadow.querySelector('#between-start-form-field'),
      $betweenDescription: this.shadow.querySelector('#middle-label'),
      $betweenEndFormField: this.shadow.querySelector('#between-end-form-field'),
    };

    this.getTranslations();
    this.setTranslatedStrings();
  }

  getTranslations() {
    this.translations = this.stringsTranslator.getSchedulerStrings();
  }

  setTranslatedStrings() {
    this.dom.$recurrenceFormField.label = htmlEncode(this.dependencies.translateFn('Recurrence'));

    // Weekly
    this.dom.$appliesEveryWeeksFormField.label = this.dependencies.translateFn('Applies every');
    this.dom.$appliesEveryWeeksDescription.innerText = htmlEncode(this.dependencies.translateFn('weeks'));
    // Weekly - On the following day(s)
    this.dom.$weeklyOnFormField.label = htmlEncode(this.dependencies.translateFn('On the following days'));

    // Monthly
    this.dom.$appliesEveryMonthsFormField.label = this.dependencies.translateFn('Applies every');
    this.dom.$appliesEveryMonthsDescription.innerText = htmlEncode(this.dependencies.translateFn('months'));
    // Monthly - On
    this.dom.$monthlyOnLabeledContent.innerText = htmlEncode(this.dependencies.translateFn('On a specific day'));
    this.dom.$monthlyFrequencyFormField.Input.placeholder = htmlEncode(this.dependencies.translateFn('Set frequency'));
    this.dom.$monthlyFrequencyDayFormField.Input.placeholder = htmlEncode(
      this.dependencies.translateFn('Select the day')
    );

    this.dom.$startingWithFormField.label = htmlEncode(this.dependencies.translateFn('Starting with'));
    this.dom.$betweenLabeledContent.innerText = htmlEncode(this.dependencies.translateFn('Between'));
    this.dom.$betweenDescription.innerText = htmlEncode(this.dependencies.translateFn('and'));
  }

  buildSuccessView(data: {
    viewData: MaintenanceWindowSchedulerSchema;
    disabled: boolean;
    hasImmediatelyOption: boolean;
  }) {
    this.createFormGroup();

    this.populateDropdownsOptions(data.hasImmediatelyOption);
    this.populateDayPicker();
    this.dom.$startingWithFormField.Input.timezone = 'UTC';
    this.dom.$startingWithFormField.Input.pickerStrings = {
      currentDay: 'Today',
    };

    this.setInitialDataInForm(data.viewData, data.hasImmediatelyOption);

    if (data.disabled) {
      this.setDisabledInnerComponents(data.disabled);
    }
  }

  setDisabledInnerComponents(disable: boolean) {
    for (const item of Object.keys(this.dom)) {
      if (this.dom[item] instanceof GzFormFieldWebComponent) {
        this.dom[item].disabled = disable;
      }
    }

    if (!disable) {
      // restore scheduler state
      this.dom.$recurrenceFormField.Input.dispatchEvent(
        new CustomEvent('triggerRevalidation', {
          detail: {},
          bubbles: true,
        })
      );
    }
  }

  populateDayPicker(): void {
    const daysArray = Object.keys(WeekDays).filter(x => isNaN(Number(x)));
    const options = Array.from(daysArray, element => ({
      label: this.translations.weekdays.shortNames[element],
      value: parseInt(WeekDays[element], 10),
    }));

    this.dom.$weeklyOnFormField.Input.setAttribute('options', JSON.stringify(options));
  }

  populateDropdownsOptions(hasImmediatelyOption: boolean): void {
    this.populateRecurrenceDropdown(hasImmediatelyOption);
    this.populateAppliesEveryWeekDropdown();
    this.populateAppliesEveryMonthDropdown();
    this.populateMonthlyFrequencyDropdown();
    this.populateMonthlyFrequencyDayDropdown();
  }

  populateRecurrenceDropdown(hasImmediatelyOption: boolean): void {
    const recurrenceOptions: Array<any> = [];

    if (hasImmediatelyOption) {
      recurrenceOptions.push({
        id: 0,
        title: this.translations.recurrence.IMMEDIATELY,
        value: String(RecurrenceOptions.IMMEDIATELY),
      });
    }

    recurrenceOptions.push(
      {
        id: 1,
        title: this.translations.recurrence.WEEKLY,
        value: String(RecurrenceOptions.WEEKLY),
      },
      {
        id: 2,
        title: this.translations.recurrence.MONTHLY,
        value: String(RecurrenceOptions.MONTHLY),
      }
    );

    this.dom.$recurrenceFormField.Input.setAttribute('options', JSON.stringify(recurrenceOptions));
  }

  populateAppliesEveryWeekDropdown(): void {
    const everyWeekDdOptions = Array.from({ length: 4 }, (_, x) => ({
      id: x + 1,
      title: String(x + 1),
      value: String(x + 1),
    }));
    this.dom.$appliesEveryWeeksFormField.Input.setAttribute('options', JSON.stringify(everyWeekDdOptions));
  }

  populateAppliesEveryMonthDropdown(): void {
    const everyMonthDdOptions = Array.from({ length: 12 }, (_, x) => ({
      id: x + 1,
      title: String(x + 1),
      value: String(x + 1),
    }));
    this.dom.$appliesEveryMonthsFormField.Input.setAttribute('options', JSON.stringify(everyMonthDdOptions));
  }

  populateMonthlyFrequencyDropdown(): void {
    const frequencyOptionsArray = Object.keys(FrequencyOptions)
      .filter(x => !isNaN(Number(x)))
      .map(key => FrequencyOptions[key]);
    const monthlyFrequencyDdOptions = Array.from(frequencyOptionsArray, (element, index) => ({
      id: index + 1,
      title: this.translations.frequency[element],
      value: String(index),
    }));
    this.dom.$monthlyFrequencyFormField.Input.setAttribute('options', JSON.stringify(monthlyFrequencyDdOptions));
  }

  populateMonthlyFrequencyDayDropdown(): void {
    const daysArray = Object.keys(WeekDays).filter(x => isNaN(Number(x)));
    const monthlyFrequencyDayDdOptions = Array.from(daysArray, element => ({
      title: this.translations.weekdays[element],
      value: WeekDays[element].toString(),
    }));
    this.dom.$monthlyFrequencyDayFormField.Input.setAttribute('options', JSON.stringify(monthlyFrequencyDayDdOptions));
  }

  private createFormGroup() {
    this.dom.$recurrenceFormField.Validators = [RequiredValidator(this.commonStrings.requiredField)];
    this.dom.$appliesEveryWeeksFormField.Validators = [RequiredValidator(this.commonStrings.requiredField)];
    this.dom.$weeklyOnFormField.Validators = [
      RequiredValidator(this.dependencies.translateFn('Select at least one day of the week.')),
    ];
    this.dom.$startingWithFormField.Validators = [RequiredValidator(this.commonStrings.requiredField)];
    this.dom.$appliesEveryMonthsFormField.Validators = [RequiredValidator(this.commonStrings.requiredField)];
    this.dom.$monthlyFrequencyFormField.Validators = [RequiredValidator(this.commonStrings.requiredField)];
    this.dom.$monthlyFrequencyDayFormField.Validators = [
      RequiredValidator(this.dependencies.translateFn('Select a day of the week.')),
    ];
    this.dom.$monthlyFrequencyDayNumberFormField.Validators = [RequiredValidator(this.commonStrings.requiredField)];
    this.dom.$betweenEndFormField.Validators = [
      TimeIntervalValidator(
        this.dom.$betweenStartFormField.Input,
        this.dependencies.translateFn('Start time has to be before end time.')
      ),
    ];

    // create FormGroup
    this.formGroup = new FormGroup({
      recurrence: this.dom.$recurrenceFormField,
      weeklyRecurrence: new FormGroup({
        every: this.dom.$appliesEveryWeeksFormField,
        days: this.dom.$weeklyOnFormField,
      }),
      monthlyRecurrence: new FormGroup({
        every: this.dom.$appliesEveryMonthsFormField,
        frequency: this.dom.$monthlyFrequencyFormField,
        frequencyDayOfWeek: this.dom.$monthlyFrequencyDayFormField,
        frequencyDayNumber: this.dom.$monthlyFrequencyDayNumberFormField,
      }),
      startingWith: this.dom.$startingWithFormField,
      between: new FormGroup({
        start: this.dom.$betweenStartFormField,
        end: this.dom.$betweenEndFormField,
      }),
    });
  }

  private setInitialDataInForm(viewData: MaintenanceWindowSchedulerSchema, hasImmediatelyOption: boolean) {
    this.formGroup.value = {
      recurrence:
        viewData?.recurrence ||
        (hasImmediatelyOption ? RecurrenceOptions.IMMEDIATELY.toString() : RecurrenceOptions.WEEKLY.toString()),
      weeklyRecurrence: {
        every: viewData?.weeklyRecurrence?.every || '1',
        days: viewData?.weeklyRecurrence?.days || [],
      },
      monthlyRecurrence: {
        every: viewData?.monthlyRecurrence?.every || '1',
        frequency: viewData?.monthlyRecurrence?.frequency || '',
        frequencyDayOfWeek: viewData?.monthlyRecurrence?.frequencyDayOfWeek || '',
        frequencyDayNumber: viewData?.monthlyRecurrence?.frequencyDayNumber || 1,
      },
      startingWith: viewData?.startingWith || '',
      between: {
        start: viewData?.between?.start || {
          hours: 0,
          minutes: 0,
        },
        end: viewData?.between?.end || {
          hours: 23,
          minutes: 59,
        },
      },
    };

    if (!this.dom.$appliesEveryWeeksFormField.Input.getFormValue()) {
      this.dom.$appliesEveryWeeksFormField.Input.setFormValue(viewData?.weeklyRecurrence?.every || '1');
    }

    if (!this.dom.$appliesEveryMonthsFormField.Input.getFormValue()) {
      this.dom.$appliesEveryMonthsFormField.Input.setFormValue(viewData?.monthlyRecurrence?.every || '1');
    }
  }

  public getFormGroup(): FormGroup {
    return this.formGroup;
  }

  setTitle(value: string) {
    this.dom.$schedulerTitle.innerText = value;
  }
}
